import { useEffect, useMemo, useRef } from "react";
import {
	FaArrowDown,
	FaArrowRight,
	FaArrowUp,
	FaRegClipboard,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { FeedbackState } from "./FeedbackState";
import { useUser } from "../context/userContext";
import { useGroup } from "../context/groupContext";

function CardInfoTableGroupSelected({ currentUniqueIdentifierSelected }) {
	const {
		getInfoGroupTableStandings,
		infoGroupTableStandings,
		loadingInfoGroupTableStandings,
		groupTableStandingsError,
	} = useGroup();
	const { userInfoLogin } = useUser();
	const getInfoGroupTableStandingsRef = useRef(getInfoGroupTableStandings);

	const currentUserSignals = useMemo(() => {
		const storedUser =
			userInfoLogin || JSON.parse(localStorage.getItem("userApuestaco") || "null");
		if (!storedUser) {
			return { identifiers: [], names: [] };
		}

		return {
			identifiers: [
				storedUser.uniqueIdentifier,
				storedUser.userIdentifier,
				storedUser.playerIdentifier,
				storedUser.id,
			].filter(Boolean),
			names: [
				storedUser.fullName,
				storedUser.playerFullName,
				storedUser.name,
				storedUser.username,
			]
				.filter(Boolean)
				.map((value) => value.toLowerCase()),
		};
	}, [userInfoLogin]);

	useEffect(() => {
		getInfoGroupTableStandingsRef.current = getInfoGroupTableStandings;
	}, [getInfoGroupTableStandings]);

	useEffect(() => {
		getInfoGroupTableStandingsRef.current(currentUniqueIdentifierSelected);
	}, [currentUniqueIdentifierSelected]);

	const getRankTrend = (item) => {
		if (item.previousRank > item.rank) {
			return {
				icon: <FaArrowUp aria-hidden='true' />,
				label: "Subio en la tabla",
				className: "standings-rank-trend standings-rank-trend-up",
			};
		}

		if (item.previousRank < item.rank) {
			return {
				icon: <FaArrowDown aria-hidden='true' />,
				label: "Bajo en la tabla",
				className: "standings-rank-trend standings-rank-trend-down",
			};
		}

		return {
			icon: <FaArrowRight aria-hidden='true' />,
			label: "Sin cambios en la tabla",
			className: "standings-rank-trend standings-rank-trend-flat",
		};
	};

	const isCurrentUserRow = (item) => {
		if (item.isCurrentUser) {
			return true;
		}

		const playerName = item.playerFullName?.toLowerCase();
		return (
			currentUserSignals.identifiers.includes(item.playerIdentifier) ||
			(playerName && currentUserSignals.names.includes(playerName))
		);
	};

	if (loadingInfoGroupTableStandings) {
		return (
			<div className='standings-skeleton-table' aria-hidden='true'>
				{Array.from({ length: 5 }).map((_, index) => (
					<div key={`standings-skeleton-${index}`} className='standings-skeleton-row'>
						<div className='standings-skeleton-cell standings-skeleton-cell-rank'></div>
						<div className='standings-skeleton-cell standings-skeleton-cell-name'></div>
						<div className='standings-skeleton-cell standings-skeleton-cell-points'></div>
						<div className='standings-skeleton-cell standings-skeleton-cell-bets'></div>
					</div>
				))}
			</div>
		);
	}

	if (groupTableStandingsError) {
		return (
			<FeedbackState
				variant='error'
				title='No pudimos cargar la tabla'
				description={groupTableStandingsError}
				actionLabel='Reintentar'
				onAction={() =>
					getInfoGroupTableStandings(currentUniqueIdentifierSelected)
				}
				compact
			/>
		);
	}

	if (!infoGroupTableStandings.Result?.standings?.length) {
		return (
			<FeedbackState
				variant='empty'
				title='Todavia no hay posiciones'
				description='La tabla aparecera cuando los integrantes empiecen a sumar puntos en el grupo.'
				compact
			/>
		);
	}

	return (
		<div className='standings-table-wrapper'>
			<table className='table standings-table align-middle mb-0'>
				<thead>
					<tr>
						<th scope='col' className='standings-col-rank'>
							#
						</th>
						<th scope='col'>Jugador</th>
						<th scope='col' className='text-center standings-col-points'>
							Puntos
						</th>
						<th scope='col' className='text-center standings-col-bets'>
							Apuestas
						</th>
					</tr>
				</thead>
				<tbody>
					{infoGroupTableStandings.Result?.standings.map((item) => {
						const rankTrend = getRankTrend(item);
						const showLiveBadge = Boolean(item.livePredictionText);
						const isCurrentUser = isCurrentUserRow(item);

						return (
							<tr
								key={item.playerIdentifier}
								className={isCurrentUser ? "standings-row-current-user" : ""}
							>
								<th className='standings-rank-cell' scope='row'>
									<div className='standings-rank-stack'>
										<span className='standings-rank-number'>{item.rank}</span>
										<span
											className={rankTrend.className}
											aria-label={rankTrend.label}
											title={rankTrend.label}
										>
											{rankTrend.icon}
										</span>
									</div>
								</th>
								<td className='standings-player-cell'>
									<div className='standings-player-main'>
										<Link
											to={`/players/${item.playerIdentifier}/groupbets/${currentUniqueIdentifierSelected}`}
											className='standings-player-link'
											aria-label={`Ver apuestas de ${item.playerFullName}`}
										>
											<FaRegClipboard aria-hidden='true' />
										</Link>
										<div className='standings-player-copy'>
											<div className='standings-player-name'>
												{item.playerFullName}
											</div>
											{isCurrentUser && (
												<span className='standings-you-badge'>Tu</span>
											)}
										</div>
									</div>
								</td>
								<td className='text-center standings-points-cell'>
									<span className='standings-metric'>{item.points}</span>
								</td>
								<td className='text-center standings-bets-cell'>
									<div className='standings-bets-stack'>
										<span className='standings-metric'>
											{item.predictionCount}
										</span>
										{showLiveBadge && (
											<span className='standings-live-badge'>
												{item.livePredictionText}
											</span>
										)}
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export { CardInfoTableGroupSelected };
